import { X } from "untitledui-js/react";
import { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@pondigitalsolutions/pon-component-library";
import { twMerge } from "tailwind-merge";
import {
  useCreateSession,
  useGetSessions,
  useDeleteSession,
  useDeleteAllSessions,
} from "@hooks/chat";
import { useUserContext } from "@hooks/useUserContext";
import { useConfig } from "@hooks/useConfig";
import { useConfigContext } from "@hooks/useConfigContext";
import { useOutsideClick } from "@hooks/useOutsideClick";

import { SearchInput } from "@components/search/SearchInput";
import { SearchContent } from "@components/search/SearchContent";
import { Modal } from "@components/shared/Modal";

import { languageDutch } from "../i18n";
import { getSlugForLanguage } from "@utils/slugUtils";
import { isHomepage } from "@utils/homepage";
import {
  ClearAllButtonProps,
  SearchContentWrapperProps,
  SearchContainerProps,
} from "../types/SearchTypes";

const ClearAllButton = ({ onClick, disabled, text }: ClearAllButtonProps) => (
  <Button
    id="delete-all-sessions-button"
    variant="soft"
    onClick={onClick}
    disabled={disabled}
    data-testid="delete-all-sessions-button"
    className="flex items-center gap-2 rounded-lg bg-white/10 px-2 py-1.5 text-xs text-white transition-colors hover:bg-white/30 disabled:opacity-50 dark:text-tertiary-400 dark:hover:bg-red-900/20 dark:hover:text-red-400"
  >
    {text}
    <X className="h-2.5 w-2.5" />
  </Button>
);

const SearchContentWrapper = ({
  searchSessionsData,
  isLoadingSearchSessions,
  searchSessionsError,
  suggestionsData,
  isLoadingSuggestions,
  searchTerm,
  searchLanguage,
  onNavigateToSession,
  onDeleteSession,
  onNavigateToSuggestion,
  clearAllButton,
}: SearchContentWrapperProps) => (
  <SearchContent
    searchSessionsData={searchSessionsData}
    isLoadingSearchSessions={isLoadingSearchSessions}
    searchSessionsError={searchSessionsError}
    suggestionsData={suggestionsData || []}
    isLoadingSuggestions={isLoadingSuggestions}
    searchTerm={searchTerm}
    searchLanguage={searchLanguage}
    onNavigateToSession={onNavigateToSession}
    onDeleteSession={onDeleteSession}
    onNavigateToSuggestion={onNavigateToSuggestion}
    clearAllButton={clearAllButton}
  />
);

export const SearchContainer = ({
  hideOnHomepage = false,
  mobileIconOnly = false,
  hideMobileIcon = false,
}: SearchContainerProps = {}) => {
  // Third party hooks
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { lang } = useParams();

  // Config context
  const { searchRef, dispatch, state } = useConfigContext();

  // Initialize outside click hook for search functionality
  useOutsideClick();

  // Refs
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Use shared input value from context instead of local state
  const inputValue = state.searchInputValue;
  const setInputValue = useCallback(
    (value: string) => {
      dispatch({ type: "SEARCH_INPUT_VALUE", payload: { value } });
    },
    [dispatch]
  );
  const [deletedSessionId, setDeletedSessionId] = useState<string | null>(null);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);

  // Helper function to build navigation path with language prefix
  const buildNavigationPath = (path: string) => {
    const currentLang = i18n.language;
    return getSlugForLanguage(path, currentLang);
  };

  // Context state
  const { data, isLoading: isLoadingSuggestions } = useConfig();
  const { user } = useUserContext();

  // Derived state
  const suggestionsData = useMemo(() => {
    return (
      user?.languagePreference === languageDutch
        ? data?.storyData?.content.searchSuggestions
        : data?.storyData?.content.searchSuggestionsEN
    ) as {
      labelOne: string;
      labelTwo: string;
    }[];
  }, [data, user?.languagePreference]);

  // React Query - query hooks
  const {
    data: searchSessionsData = [],
    isLoading: isLoadingSearchSessions,
    error: searchSessionsError,
  } = useGetSessions();

  // React Query - mutation hooks
  const { mutate: createSession, isPending: isCreatingSession } =
    useCreateSession();

  const { mutate: deleteSession, isPending: isDeletingSession } =
    useDeleteSession();

  const { mutate: deleteAllSessions, isPending: isDeletingAllSessions } =
    useDeleteAllSessions();

  // Check if we're on the homepage (only when hideOnHomepage is true)
  const isHomepagePage = hideOnHomepage && isHomepage(location.pathname, lang);

  // Clear input when navigating away from search pages
  useEffect(() => {
    const isSearchPage = location.pathname.includes("/search/");

    // If we're not on a search page, clear the input
    if (!isSearchPage) {
      setInputValue("");
    }
  }, [location.pathname, setInputValue]);

  // Don't render on homepage when hideOnHomepage is enabled
  if (isHomepagePage) {
    return null;
  }

  // Event handlers
  const handleInputChange = (value: string) => {
    setInputValue(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if ((inputValue || "").trim() !== "") {
        handleSendMessage(inputValue);
      }
    }
  };

  const handleSendMessage = (message: string) => {
    // Set input to show the search term immediately
    setInputValue(message);

    // Create new session and navigate to it
    createSession(undefined, {
      onSuccess: (data) => {
        // Navigate to the new chat URL with the message as query param
        const searchPath = `/search/${data.id}?message=${encodeURIComponent(message)}`;
        void navigate(buildNavigationPath(searchPath));
        // Close the search dropdown after navigation
        dispatch({ type: "SEARCH_VISIBILITY", payload: { visible: false } });
      },
      onError: (error) => {
        console.error("Failed to create session:", error);
      },
    });
  };

  const handleDeleteSession = (sessionId: string) => {
    deleteSession(sessionId, {
      onError: (error) => {
        console.error("Failed to delete session:", error);
      },
      onSettled: () => {
        setDeletedSessionId(null);
      },
    });
  };

  // Handle bulk deletion
  const handleDeleteAllSessions = () => {
    deleteAllSessions(undefined, {
      onError: (error) => {
        console.error("Failed to delete all sessions:", error);
      },
      onSettled: () => {
        setShowBulkDeleteConfirm(false);
      },
    });
  };

  const clearAllButton = (
    <ClearAllButton
      onClick={() => setShowBulkDeleteConfirm(true)}
      disabled={isDeletingAllSessions}
      text={t("OmniSearch.clearAll")}
    />
  );

  const searchContentWrapperProps = {
    searchSessionsData,
    isLoadingSearchSessions,
    searchSessionsError,
    suggestionsData: suggestionsData || [],
    isLoadingSuggestions,
    searchTerm: inputValue || "",
    searchLanguage: user?.languagePreference,
    onNavigateToSession: (sessionId: string) => {
      // Find the session to get its name/search term
      const session = searchSessionsData.find((s) => s.id === sessionId);
      const searchTerm = session?.name || t("OmniSearch.untitledChat");

      void navigate(buildNavigationPath(`/search/${sessionId}`));
      // Close the search input and show the search term from history
      dispatch({
        type: "SEARCH_VISIBILITY",
        payload: { visible: false },
      });
      // Only set the input value if it's not the default "untitled" text
      if (searchTerm !== t("OmniSearch.untitledChat")) {
        setInputValue(searchTerm);
      } else {
        setInputValue("");
      }
    },
    onDeleteSession: setDeletedSessionId,
    onNavigateToSuggestion: (suggestionId: string) => {
      // Extract index from suggestionId
      const index = parseInt(suggestionId.replace("suggestion-", ""));
      const suggestion = suggestionsData[index];
      if (suggestion) {
        // Set input value and trigger search
        setInputValue(suggestion.labelOne);
        // Send the message with the suggestion name
        handleSendMessage(suggestion.labelOne);
      }
    },
    clearAllButton,
  };

  return mobileIconOnly ? (
    <SearchInput
      ref={textareaRef}
      value={inputValue}
      onChange={handleInputChange}
      onKeyDown={handleKeyDown}
      onSend={() => handleSendMessage(inputValue)}
      isSending={isCreatingSession}
      isHomepage={!hideOnHomepage}
      mobileIconOnly={true}
      hideMobileIcon={hideMobileIcon}
    >
      {() => <SearchContentWrapper {...searchContentWrapperProps} />}
    </SearchInput>
  ) : (
    <div ref={searchRef} className="relative z-1 flex flex-col">
      <div
        className={twMerge(
          "flex items-center justify-center gap-3",
          !hideOnHomepage && "lg:px-16",
          !isHomepage && "px-4"
        )}
      >
        <div
          className={twMerge(
            "flex",
            "justify-center",
            (() => {
              if (!hideOnHomepage) {
                return "w-full";
              }

              const hasValue = (inputValue || "").trim().length > 0;
              const shouldExpand = state.isSearchOpen || hasValue;
              return shouldExpand ? "w-full" : "w-80";
            })(),
            "transform-gpu transition-all duration-300 ease-in-out will-change-[width]"
          )}
        >
          <SearchInput
            ref={textareaRef}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onSend={() => handleSendMessage(inputValue)}
            isSending={isCreatingSession}
            isHomepage={!hideOnHomepage}
          >
            {() => <SearchContentWrapper {...searchContentWrapperProps} />}
          </SearchInput>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deletedSessionId && (
        <Modal
          isOpen={true}
          onClose={() => setDeletedSessionId(null)}
          title={t("OmniSearch.deleteChat")}
          size="sm"
        >
          <div className="mb-6">
            <p className="text-sm text-tertiary-400 dark:text-tertiary-300">
              {t("OmniSearch.deleteChatConfirmation")}
            </p>
          </div>

          <div className="flex gap-3">
            <Button
              id={"cancel-delete-button"}
              variant="secondary"
              size="lg"
              onClick={() => setDeletedSessionId(null)}
              disabled={isDeletingSession}
            >
              {t("OmniSearch.cancel")}
            </Button>
            <Button
              id="confirm-delete-button"
              variant="primary"
              size="lg"
              onClick={() => handleDeleteSession(deletedSessionId)}
              disabled={isDeletingSession}
            >
              {isDeletingSession
                ? t("OmniSearch.deleting")
                : t("OmniSearch.delete")}
            </Button>
          </div>
        </Modal>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteConfirm && (
        <Modal
          isOpen={true}
          onClose={() => setShowBulkDeleteConfirm(false)}
          title={t("OmniSearch.clearAllChats")}
          size="sm"
          data-testid="bulk-delete-modal"
        >
          <div className="mb-6">
            <p className="text-sm text-tertiary-400 dark:text-tertiary-300">
              {t("OmniSearch.clearAllConfirmation")}
            </p>
          </div>

          <div className="flex gap-3">
            <Button
              id="bulk-delete-cancel"
              variant="secondary"
              size="lg"
              onClick={() => setShowBulkDeleteConfirm(false)}
              disabled={isDeletingAllSessions}
              data-testid="bulk-delete-cancel"
            >
              {t("OmniSearch.cancel")}
            </Button>
            <Button
              id="bulk-delete-confirm"
              variant="primary"
              size="lg"
              onClick={handleDeleteAllSessions}
              disabled={isDeletingAllSessions}
              data-testid="bulk-delete-confirm"
            >
              {isDeletingAllSessions
                ? t("OmniSearch.clearing")
                : t("OmniSearch.clearAll")}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
};
